import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch, getToken, setToken } from './api';

interface AuthState {
  token: string | null;
  email: string | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  loading: boolean;
}

interface AuthCtx extends AuthState {
  sendCode: (email: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<void>;
  logout: () => void;
}

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function stateFromToken(token: string | null): AuthState {
  if (!token)
    return { token: null, email: null, isAdmin: false, isSuperAdmin: false, loading: false };
  const claims = parseJwt(token);
  if (!claims)
    return { token: null, email: null, isAdmin: false, isSuperAdmin: false, loading: false };
  const exp = (claims.exp as number | undefined) ?? 0;
  if (exp * 1000 < Date.now()) {
    setToken(null);
    return { token: null, email: null, isAdmin: false, isSuperAdmin: false, loading: false };
  }
  return {
    token,
    email: (claims.email as string) ?? (claims.sub as string) ?? null,
    isAdmin: claims.is_admin === true,
    isSuperAdmin: claims.is_super_admin === true,
    loading: false,
  };
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => stateFromToken(getToken()));

  // Refresh state if another tab logs out
  useEffect(() => {
    const onStorage = () => setState(stateFromToken(getToken()));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const sendCode = async (email: string) => {
    await apiFetch<{ ok: true }>('/sendCode', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  };

  const verifyCode = async (email: string, code: string) => {
    const res = await apiFetch<{ token: string }>('/verifyCode', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
    setToken(res.token);
    setState(stateFromToken(res.token));
  };

  const logout = () => {
    setToken(null);
    setState(stateFromToken(null));
  };

  return createElement(
    Ctx.Provider,
    { value: { ...state, sendCode, verifyCode, logout } },
    children,
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
}
